import { useMemo, useState } from "react";

import first from "lodash/first";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import {
  AnalyticsFieldName,
  SMTAnalyticsSource,
  SMTEntryAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { ModalPath, TabPath } from "@/constants/Navigation";
import { NavTestID, SMTTestID, TextTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import useAppUserSMTPermissions from "@/hooks/useAppUserSMTPermissions";
import { useAnalytics } from "@/services/Analytics";
import { useGetSMTSummaryQuery } from "@/store/api/smt";
import { useAppUserSelector } from "@/store/reducers/appUser";
import { useSMTDispatch } from "@/store/reducers/smt";
import SMTWeekLog from "@/ui/components/smt/SMTWeekLogSection/SMTWeekLog";
import { Button, FAIconName, Text, UserTabSelector } from "@/ui/core";
import { Box, HStack, VStack } from "@/ui/layout";
import { LayoutToken } from "@/ui/theme/tokens";
import {
  getDateRangeForWeekSummary,
  mapSMTSummaryResponseToDates,
} from "@/utils/smt.util";
import { joinTestIDs } from "@/utils/string.util";
import { getTabSelectorOptions } from "@/utils/user.util";

enum SMTWeekLogSectionContent {
  Title = "This week's entries",
  ViewAllEntriesButtonTitle = "View all entries",
}

const SMTWeekLogSection = () => {
  const baseTestID = joinTestIDs(SMTTestID.Primary, SMTTestID.WeekLogSection);

  const { track, trackUserSelectorEvent } = useAnalytics();
  const { endDate, startDate } = getDateRangeForWeekSummary();
  const { setAddSMTEntrySelectedPatientId, setIsFilePickerModalOpen } =
    useSMTDispatch();

  const {
    externalId: appUserId,
    isPatient: isAppUserPatient,
    isSupport: isAppUserSupport,
  } = useAppUserSelector();
  const {
    shouldAppUserSeeAddSMTEntriesButton,
    supportPatientsEligibleForSMT: {
      supportPatientsWithSMTAddAccess,
      supportPatientsWithSMTViewAccess,
    },
  } = useAppUserSMTPermissions();

  const initialUserId = isAppUserPatient
    ? appUserId
    : first(supportPatientsWithSMTViewAccess)?.externalId;

  const [selectedUserId, setSelectedUserId] = useState<string>(
    initialUserId || "",
  );

  const userTabs = useMemo(() => {
    return (
      getTabSelectorOptions({
        appUserCount: 0,
        appUserId,
        isAppUserPatient,
        patients: supportPatientsWithSMTViewAccess,
        shouldIncludeSelf: false,
      }) || []
    );
  }, [appUserId, isAppUserPatient, supportPatientsWithSMTViewAccess]);

  // TODO: Handle support user patient selection - https://teamequip.atlassian.net/browse/JN-1909
  const { data: summaryResponse } = useGetSMTSummaryQuery(
    {
      endDate,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- selectedUserId is set before this query can run
      patientId: selectedUserId!,
      startDate,
    },
    {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- skip is a valid option
      skip: isEmpty(selectedUserId),
    },
  );

  const datesWithLogs = summaryResponse?.entries
    ? mapSMTSummaryResponseToDates(summaryResponse.entries)
    : [];

  const globalStreak = summaryResponse?.globalStreak ?? 0;

  const handleAddEntryPress = () => {
    if (isAppUserSupport) {
      setAddSMTEntrySelectedPatientId(selectedUserId);
    }

    setIsFilePickerModalOpen(true);

    track(SMTEntryAnalyticsEvent.AddSMTEntryPressed, {
      [AnalyticsFieldName.Component]: SMTAnalyticsSource.WeekLogCard,
      [AnalyticsFieldName.Source]: TabPath.Home,
    });
  };

  const handleViewAllEntriesPress = () => {
    track(SMTEntryAnalyticsEvent.ViewAllEntriesPressed, {
      [AnalyticsFieldName.Component]: SMTAnalyticsSource.WeekLogCard,
      [AnalyticsFieldName.Source]: TabPath.Home,
    });
    router.navigate({
      params: { parentSelectedUserId: selectedUserId },
      pathname: ModalPath.Journal,
    });
  };

  const handleUserChange = (userId: string) => {
    setSelectedUserId(userId);
    trackUserSelectorEvent({
      component: SMTWeekLogSection.name,
      selectedUser: userId,
      source: SMTAnalyticsSource.WeekLogCard,
    });
  };

  const canAddEntries = useMemo(() => {
    if (isAppUserPatient) {
      return shouldAppUserSeeAddSMTEntriesButton;
    }
    const selectedPatient = supportPatientsWithSMTAddAccess.find(
      (patient) => patient.externalId === selectedUserId,
    );
    return !isNil(selectedPatient);
  }, [
    isAppUserPatient,
    selectedUserId,
    supportPatientsWithSMTAddAccess,
    shouldAppUserSeeAddSMTEntriesButton,
  ]);

  return (
    <Box paddingHorizontal={LayoutToken.ContentPaddingHorizontal}>
      <VStack gap={18} width="100%">
        <HStack alignItems="center" justifyContent="space-between">
          <Text testID={joinTestIDs(baseTestID, TextTestID.Title)} variant="h5">
            {SMTWeekLogSectionContent.Title}
          </Text>
          <UserTabSelector
            accessibilityLabel="User Tab Selector"
            appUserId={appUserId}
            onUserChange={handleUserChange}
            tabs={userTabs}
            testID={joinTestIDs(baseTestID, NavTestID.TabSelector)}
          />
        </HStack>
        <SMTWeekLog
          canAddEntries={canAddEntries}
          datesWithLogs={datesWithLogs}
          globalStreak={globalStreak}
          onAddEntryPress={handleAddEntryPress}
        />
        <Box alignItems="center" width="100%">
          <Button
            onPress={handleViewAllEntriesPress}
            rightIcon={FAIconName.ArrowRight}
            testID={joinTestIDs(baseTestID, "view-all-entries")}
            title={SMTWeekLogSectionContent.ViewAllEntriesButtonTitle}
            variant="tertiary"
          />
        </Box>
      </VStack>
    </Box>
  );
};

export { SMTWeekLogSectionContent };
export default SMTWeekLogSection;
